<template>
  <div
    ref="root"
    class="dv-shell"
    :class="{ 'dv-shell--fullscreen': isFullscreen }"
    @keydown="onKeydown">
    <ViewerToolbar
      v-if="toolbar !== 'none' && document"
      ref="toolbar"
      :capabilities="capabilities"
      :state="viewState"
      :actions="actions"
      :compact="toolbar === 'compact'"
      :search-open="searchOpen"
      :is-fullscreen="isFullscreen"
      :downloading="downloading"
      @command="command"
      @action="onAction" />

    <div class="dv-shell__body">
      <component
        :is="rendererComponent"
        v-if="document"
        :key="renderKey"
        ref="renderer"
        :document="document"
        @loading="onLoading"
        @ready="onReady"
        @error="onError"
        @capabilities="onCapabilities"
        @state="onState"
        @notice="onNotice"
        @download="download" />

      <div v-if="!document" class="dv-shell__state">
        <v-icon large class="dv-shell__state-icon">mdi-file-outline</v-icon>
        <p>No document selected.</p>
      </div>
      <div v-else-if="errorMessage" class="dv-shell__state dv-shell__state--overlay" role="alert">
        <v-icon large class="dv-shell__state-icon">mdi-file-alert-outline</v-icon>
        <p class="dv-shell__state-text">{{ errorMessage }}</p>
        <div class="dv-shell__state-actions">
          <v-btn small outlined color="primary" @click="retry">
            <v-icon small left>mdi-refresh</v-icon>
            Try again
          </v-btn>
          <v-btn small depressed color="primary" :loading="downloading" @click="download">
            <v-icon small left>mdi-download</v-icon>
            Download
          </v-btn>
        </div>
      </div>
      <div v-else-if="loading" class="dv-shell__state dv-shell__state--overlay" aria-busy="true">
        <v-progress-circular indeterminate size="40" width="3" color="primary" />
        <p class="dv-shell__state-text">Loading {{ kindLabel }}…</p>
      </div>
    </div>

    <div class="dv-shell__live" aria-live="polite">{{ liveMessage }}</div>
  </div>
</template>

<script>
  import ViewerToolbar from './ViewerToolbar.vue';
  import UnsupportedRenderer from './renderers/UnsupportedRenderer.vue';
  import { DOCUMENT_KIND, resolveKind, documentRenderKey } from './documentKinds';
  import { downloadDocument, standaloneDocumentRoute } from './documentSource';

  /**
   * Shown when a renderer's chunk cannot be downloaded (offline, or a deploy replaced the chunk
   * files). It receives the renderer's listeners, so the shell shows its normal error state.
   */
  const RendererLoadError = {
    name: 'RendererLoadError',
    mounted() {
      this.$emit('error', 'The viewer could not be loaded. Check your connection, or reload the page.');
    },
    render(h) {
      return h('div');
    },
  };

  const lazy = (load) => () => ({ component: load(), error: RendererLoadError, timeout: 60000 });

  /** One lazy chunk per renderer: a session that only opens images never loads pdf.js. */
  const RENDERERS = Object.freeze({
    [DOCUMENT_KIND.PDF]: lazy(() => import(/* webpackChunkName: "doc-viewer-pdf" */ './renderers/PdfRenderer.vue')),
    [DOCUMENT_KIND.IMAGE]: lazy(() => import(/* webpackChunkName: "doc-viewer" */ './renderers/ImageRenderer.vue')),
    [DOCUMENT_KIND.OFFICE]: lazy(() => import(/* webpackChunkName: "doc-viewer" */ './renderers/OfficeRenderer.vue')),
    [DOCUMENT_KIND.TEXT]: lazy(() => import(/* webpackChunkName: "doc-viewer-text" */ './renderers/TextRenderer.vue')),
    [DOCUMENT_KIND.CSV]: lazy(() => import(/* webpackChunkName: "doc-viewer-text" */ './renderers/CsvRenderer.vue')),
    [DOCUMENT_KIND.JSON]: lazy(() => import(/* webpackChunkName: "doc-viewer-text" */ './renderers/JsonXmlRenderer.vue')),
    [DOCUMENT_KIND.XML]: lazy(() => import(/* webpackChunkName: "doc-viewer-text" */ './renderers/JsonXmlRenderer.vue')),
    [DOCUMENT_KIND.VIDEO]: lazy(() => import(/* webpackChunkName: "doc-viewer" */ './renderers/MediaRenderer.vue')),
    [DOCUMENT_KIND.AUDIO]: lazy(() => import(/* webpackChunkName: "doc-viewer" */ './renderers/MediaRenderer.vue')),
  });

  const KIND_LABELS = Object.freeze({
    [DOCUMENT_KIND.PDF]: 'PDF',
    [DOCUMENT_KIND.IMAGE]: 'image',
    [DOCUMENT_KIND.OFFICE]: 'Office preview',
    [DOCUMENT_KIND.VIDEO]: 'video',
    [DOCUMENT_KIND.AUDIO]: 'audio',
  });

  function isTypingTarget(target) {
    const tag = target?.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable;
  }

  /**
   * The document viewer. Picks a renderer for the file (documentKinds.resolveKind), and owns the
   * toolbar, the loading / error / unsupported states, keyboard shortcuts, full screen and download.
   *
   * RENDERER CONTRACT. A renderer takes a `document` prop and:
   *  - emits `loading` (Boolean), then `ready` or `error` (a message for the user)
   *  - emits `capabilities` ({ zoom, rotate, pages, search, sidebar, print, wrap }) once it knows them
   *  - emits `state` with any of { page, pageCount, scale, scaleValue, match, sidebarOpen, wrap, printing }
   *  - may emit `notice` ({ type, text }) and `download`
   *  - implements `command(name, payload)`: zoomIn, zoomOut, setZoom, rotate, goToPage, search,
   *    clearSearch, toggleSidebar, toggleWrap, print, cancelPrint. Unknown commands are ignored.
   *
   * The `document` is a DocumentVM-like object. `viewRevisionId` selects an older version.
   */
  export default {
    name: 'DocumentViewerShell',
    components: { ViewerToolbar },
    props: {
      document: { type: Object, default: null },
      /** 'full', 'compact' (narrow places: secondary actions go into a menu) or 'none' */
      toolbar: { type: String, default: 'full' },
      allowDownload: { type: Boolean, default: true },
      allowNewTab: { type: Boolean, default: true },
      allowFullscreen: { type: Boolean, default: true },
    },
    data() {
      return {
        loading: true,
        errorMessage: '',
        capabilities: {},
        viewState: {},
        searchOpen: false,
        isFullscreen: false,
        downloading: false,
        retryCount: 0,
        liveMessage: '',
      };
    },
    computed: {
      kind() {
        return resolveKind(this.document);
      },
      kindLabel() {
        return KIND_LABELS[this.kind] || 'file';
      },
      rendererComponent() {
        return RENDERERS[this.kind] || UnsupportedRenderer;
      },
      renderKey() {
        return `${documentRenderKey(this.document)}#${this.retryCount}`;
      },
      facilityId() {
        return this.$route?.params?.id || this.$store?.state?.assetId || null;
      },
      newTabRoute() {
        return this.allowNewTab ? standaloneDocumentRoute(this.document, this.facilityId) : null;
      },
      actions() {
        return {
          download: this.allowDownload,
          newTab: !!this.newTabRoute,
          fullscreen: this.allowFullscreen && !!window.document.fullscreenEnabled,
        };
      },
    },
    watch: {
      renderKey: {
        immediate: true,
        handler() {
          this.loading = true;
          this.errorMessage = '';
          this.capabilities = {};
          this.viewState = {};
          this.searchOpen = false;
        },
      },
      'viewState.page'(page) {
        if (page && this.viewState.pageCount) this.liveMessage = `Page ${page} of ${this.viewState.pageCount}`;
      },
    },
    mounted() {
      window.document.addEventListener('fullscreenchange', this.onFullscreenChange);
    },
    beforeDestroy() {
      window.document.removeEventListener('fullscreenchange', this.onFullscreenChange);
      if (this.isFullscreen) window.document.exitFullscreen?.().catch(() => {});
    },
    methods: {
      onLoading(value) {
        this.loading = !!value;
      },
      onReady() {
        this.loading = false;
        this.errorMessage = '';
        this.$emit('ready');
      },
      onError(message) {
        this.loading = false;
        this.errorMessage = message || 'The document could not be shown.';
        this.$emit('error', this.errorMessage);
      },
      onCapabilities(capabilities) {
        this.capabilities = { ...capabilities };
      },
      onState(partial) {
        this.viewState = { ...this.viewState, ...partial };
      },
      onNotice({ type = 'info', text } = {}) {
        if (text) this.$dialog?.notify?.[type]?.(text, { position: 'top-right', timeout: 4000 });
      },
      retry() {
        this.retryCount += 1;
      },
      /** Forward a command to the renderer. Also callable from outside through a ref. */
      command(name, payload) {
        this.$refs.renderer?.command?.(name, payload);
      },
      onAction(name) {
        switch (name) {
          case 'openSearch':
            this.openSearch();
            break;
          case 'closeSearch':
            this.closeSearch();
            break;
          case 'download':
            this.download();
            break;
          case 'newTab':
            this.openInNewTab();
            break;
          case 'fullscreen':
            this.toggleFullscreen();
            break;
          default:
            break;
        }
      },
      openSearch() {
        if (!this.capabilities.search) return;
        if (this.searchOpen) this.$refs.toolbar?.focusSearch();
        this.searchOpen = true;
      },
      closeSearch() {
        this.searchOpen = false;
        this.command('clearSearch');
        this.$refs.root?.focus?.();
      },
      async download() {
        if (this.downloading || !this.document) return;
        this.downloading = true;
        try {
          const ok = await downloadDocument(this.document);
          if (!ok) this.onNotice({ type: 'error', text: 'The file could not be downloaded.' });
        } finally {
          this.downloading = false;
        }
      },
      openInNewTab() {
        if (!this.newTabRoute) return;
        const { href } = this.$router.resolve(this.newTabRoute);
        window.open(href, '_blank', 'noopener');
      },
      toggleFullscreen() {
        if (window.document.fullscreenElement) window.document.exitFullscreen?.();
        else this.$refs.root?.requestFullscreen?.().catch(() => {});
      },
      onFullscreenChange() {
        this.isFullscreen = window.document.fullscreenElement === this.$refs.root;
      },
      onKeydown(event) {
        const mod = event.ctrlKey || event.metaKey;
        const key = event.key;
        if (mod && (key === 'f' || key === 'F') && this.capabilities.search) {
          event.preventDefault();
          this.openSearch();
          return;
        }
        if (mod && (key === '=' || key === '+') && this.capabilities.zoom) {
          event.preventDefault();
          this.command('zoomIn');
          return;
        }
        if (mod && key === '-' && this.capabilities.zoom) {
          event.preventDefault();
          this.command('zoomOut');
          return;
        }
        if (mod && key === '0' && this.capabilities.zoom) {
          event.preventDefault();
          this.command('setZoom', 'page-fit');
          return;
        }
        if (mod || event.altKey || isTypingTarget(event.target)) return;
        if ((key === 'r' || key === 'R') && this.capabilities.rotate) {
          this.command('rotate', event.shiftKey ? -90 : 90);
        } else if (key === 'Home' && this.capabilities.pages) {
          event.preventDefault();
          this.command('goToPage', 1);
        } else if (key === 'End' && this.capabilities.pages) {
          event.preventDefault();
          this.command('goToPage', this.viewState.pageCount);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .dv-shell {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    outline: none;
    background: var(--v-background-base, #ffffff);

    &--fullscreen {
      width: 100vw;
      height: 100vh;
    }
  }

  .dv-shell__body {
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
  }

  .dv-shell__state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    height: 100%;
    padding: 24px;
    text-align: center;

    &--overlay {
      position: absolute;
      inset: 0;
      z-index: 3;
      background: var(--v-background-base, #ffffff);
    }
  }

  .dv-shell__state-icon {
    opacity: 0.55;
  }

  .dv-shell__state-text {
    max-width: 460px;
    margin: 0;
    opacity: 0.85;
  }

  .dv-shell__state-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .dv-shell__live {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
</style>
